import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  Notifications as NotificationsIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  fetchInterventions,
  fetchMonitoringErrors,
  fetchStats,
  fetchRecentActivity,
  fetchAgdrefPending,
} from '../redux/slices/cyberDashboardSlice';
import { InterventionRequise, MonitoringError, AgdrefPending, RecentActivity } from '../types';

const CyberDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    interventions,
    interventionsTotal,
    monitoringErrors,
    stats,
    recentActivity,
    agdrefPending,
    loading,
    error,
  } = useAppSelector((state) => state.cyberDashboard);

  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = () => {
    dispatch(fetchStats());
    dispatch(fetchInterventions({ limit: 50 }));
    dispatch(fetchMonitoringErrors({ days: 7, limit: 50 }));
    dispatch(fetchRecentActivity({ limit: 20 }));
    dispatch(fetchAgdrefPending());
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const getThemeColor = (theme: string) => {
    const colors: Record<string, string> = {
      information: 'info',
      action_requise: 'warning',
      relance: 'error',
      notification_client: 'success',
      reponse_organisme: 'primary',
      autre: 'default',
    };
    return colors[theme] || 'default';
  };

  const getThemeLabel = (theme: string) => {
    const labels: Record<string, string> = {
      information: 'Information',
      action_requise: 'Action requise',
      relance: 'Relance',
      notification_client: 'Notification client',
      reponse_organisme: 'Réponse organisme',
      autre: 'Autre',
    };
    return labels[theme] || theme;
  };

  // Composant de carte mobile pour les interventions
  const InterventionCard = ({ intervention }: { intervention: InterventionRequise }) => (
    <Card sx={{ mb: 2 }} onClick={() => navigate(`/demarches/${intervention.id}`)}>
      <CardContent>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {intervention.client_nom} {intervention.client_prenom}
            </Typography>
            <Chip label={intervention.statut} size="small" />
          </Box>

          <Typography variant="body2" color="text.secondary">
            {intervention.titre}
          </Typography>

          {intervention.type_demarche_nom && (
            <Chip label={intervention.type_demarche_nom} size="small" variant="outlined" />
          )}

          {intervention.telephone_principal && (
            <Typography variant="caption">
              📞 {intervention.telephone_principal}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {intervention.nb_notes_action_recentes > 0 && (
              <Chip
                icon={<WarningIcon />}
                label={`${intervention.nb_notes_action_recentes} note(s)`}
                color="warning"
                size="small"
              />
            )}
            {intervention.nb_erreurs_monitoring_recentes > 0 && (
              <Chip
                icon={<ErrorIcon />}
                label={`${intervention.nb_erreurs_monitoring_recentes} erreur(s)`}
                color="error"
                size="small"
              />
            )}
          </Box>

          {intervention.date_limite && (
            <Typography variant="caption" color="error">
              ⏰ Limite: {new Date(intervention.date_limite).toLocaleDateString('fr-FR')}
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  // Composant de carte mobile pour les erreurs de monitoring
  const MonitoringErrorCard = ({ error: errorItem }: { error: MonitoringError }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {errorItem.client_nom} {errorItem.client_prenom}
            </Typography>
            <Chip label={errorItem.type_verification} size="small" variant="outlined" />
          </Box>

          <Typography variant="body2">{errorItem.demarche_titre}</Typography>

          {errorItem.telephone_principal && (
            <Typography variant="caption">📞 {errorItem.telephone_principal}</Typography>
          )}

          {errorItem.ancien_statut && errorItem.nouveau_statut && (
            <Typography variant="caption" color="text.secondary">
              {errorItem.ancien_statut} → {errorItem.nouveau_statut}
            </Typography>
          )}

          <Alert severity="error" sx={{ py: 0.5 }}>
            <Typography variant="caption">{errorItem.error_message || '-'}</Typography>
          </Alert>

          <Typography variant="caption" color="text.secondary">
            {new Date(errorItem.created_at).toLocaleString('fr-FR')}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );

  // Composant de carte mobile pour AGDREF
  const AgdrefCard = ({ client }: { client: AgdrefPending }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={1}>
          <Typography variant="subtitle1" fontWeight="bold">
            {client.nom} {client.prenom}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={client.numero_agdref ? 'Définitif' : 'Temporaire'}
              color={client.numero_agdref ? 'success' : 'warning'}
              size="small"
            />
            {client.est_numero_etranger && <Chip label="Num. étranger" size="small" />}
            {client.nb_alertes_agdref > 0 && (
              <Chip
                label={`${client.nb_alertes_agdref} alerte(s)`}
                color="error"
                size="small"
              />
            )}
          </Box>

          {client.telephone_principal && (
            <Typography variant="body2">📞 {client.telephone_principal}</Typography>
          )}

          {client.email && (
            <Typography variant="body2">✉️ {client.email}</Typography>
          )}

          <Typography variant="caption" color="text.secondary">
            AGDREF: {client.numero_agdref || 'En attente'}
          </Typography>

          {client.demarche_titre && (
            <Box>
              <Typography variant="caption" fontWeight="bold">Démarche liée:</Typography>
              <Typography variant="caption" display="block">{client.demarche_titre}</Typography>
              <Chip label={client.demarche_statut} size="small" sx={{ mt: 0.5 }} />
            </Box>
          )}

          <Typography variant="caption" color="text.secondary">
            Créé le {new Date(client.created_at).toLocaleDateString('fr-FR')}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );

  // Composant de carte mobile pour l'activité récente
  const ActivityCard = ({ activity }: { activity: RecentActivity }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
            <Chip
              label={activity.type === 'note' ? 'Note' : 'Monitoring'}
              color={activity.type === 'note' ? 'primary' : 'secondary'}
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              {new Date(activity.created_at).toLocaleString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          </Box>

          <Typography variant="subtitle2" fontWeight="bold">
            {activity.client_nom} {activity.client_prenom}
          </Typography>

          <Typography variant="body2" color="text.secondary">
            {activity.demarche_titre}
          </Typography>

          {activity.type === 'note' ? (
            <Box>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                <Chip
                  label={getThemeLabel(activity.theme || '')}
                  color={getThemeColor(activity.theme || '') as any}
                  size="small"
                />
                {activity.est_automatique && <Chip label="Auto" size="small" variant="outlined" />}
              </Box>
              <Typography variant="body2">{activity.contenu}</Typography>
            </Box>
          ) : (
            <Box>
              <Chip label={activity.type_verification} size="small" variant="outlined" />
              {activity.ancien_statut && activity.nouveau_statut && (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                  {activity.ancien_statut} → {activity.nouveau_statut}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ mb: { xs: 2, md: 4 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1">
          {isMobile ? 'Dashboard' : 'Tableau de bord Cyber'}
        </Typography>
        <Tooltip title="Actualiser">
          <IconButton color="primary" onClick={loadDashboardData} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading && !stats ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Statistiques générales */}
          {stats && (
            <>
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
                Vue d'ensemble
              </Typography>
              <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PeopleIcon color="primary" sx={{ mr: 1 }} />
                        <Typography color="textSecondary" variant="body2">
                          Clients Total
                        </Typography>
                      </Box>
                      <Typography variant="h4">{stats.general.nb_clients_total}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Temp: {stats.general.nb_comptes_temporaires} | Déf:{' '}
                        {stats.general.nb_comptes_definitifs}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                        <Typography color="textSecondary" variant="body2">
                          Démarches
                        </Typography>
                      </Box>
                      <Typography variant="h4">{stats.general.nb_demarches_total}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        En cours: {stats.general.nb_demarches_en_cours}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <WarningIcon color="warning" sx={{ mr: 1 }} />
                        <Typography color="textSecondary" variant="body2">
                          Interventions requises
                        </Typography>
                      </Box>
                      <Typography variant="h4" color="warning.main">
                        {stats.general.nb_interventions_requises}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <TrendingUpIcon color="success" sx={{ mr: 1 }} />
                        <Typography color="textSecondary" variant="body2">
                          Monitoring actif
                        </Typography>
                      </Box>
                      <Typography variant="h4" color="success.main">
                        {stats.general.nb_monitoring_actif}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Statistiques 24h */}
              <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 2 }}>
                Monitoring - Dernières 24h
              </Typography>
              <Grid container spacing={{ xs: 2, md: 3 }} sx={{ mb: 4 }}>
                <Grid item xs={6} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Vérifications
                      </Typography>
                      <Typography variant="h5">
                        {stats.monitoring_24h.total_verifications}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Changements de statut
                      </Typography>
                      <Typography variant="h5" color="info.main">
                        {stats.monitoring_24h.nb_changements_statut}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Nouvelles notifications
                      </Typography>
                      <Typography variant="h5" color="success.main">
                        {stats.monitoring_24h.nb_nouvelles_notifications}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        Erreurs
                      </Typography>
                      <Typography variant="h5" color="error.main">
                        {stats.monitoring_24h.nb_erreurs}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </>
          )}

          {/* Onglets */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant={isMobile ? 'scrollable' : 'fullWidth'}
              scrollButtons={isMobile ? 'auto' : false}
              allowScrollButtonsMobile
            >
              <Tab
                label={
                  isMobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <WarningIcon fontSize="small" />
                      <span>Interventions ({interventionsTotal})</span>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <WarningIcon fontSize="small" />
                      Interventions requises ({interventionsTotal})
                    </Box>
                  )
                }
              />
              <Tab
                label={
                  isMobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ErrorIcon fontSize="small" />
                      <span>Erreurs ({monitoringErrors.length})</span>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ErrorIcon fontSize="small" />
                      Erreurs monitoring ({monitoringErrors.length})
                    </Box>
                  )
                }
              />
              <Tab
                label={
                  isMobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <InfoIcon fontSize="small" />
                      <span>AGDREF ({agdrefPending.length})</span>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoIcon fontSize="small" />
                      AGDREF en attente ({agdrefPending.length})
                    </Box>
                  )
                }
              />
              <Tab
                label={
                  isMobile ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <NotificationsIcon fontSize="small" />
                      <span>Activité ({recentActivity.length})</span>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <NotificationsIcon fontSize="small" />
                      Activité récente ({recentActivity.length})
                    </Box>
                  )
                }
              />
            </Tabs>
          </Paper>

          {/* Interventions requises */}
          {activeTab === 0 && (
            <>
              {interventions.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Aucune intervention requise
                  </Typography>
                </Paper>
              ) : isMobile ? (
                <Box>
                  {interventions.map((intervention: InterventionRequise) => (
                    <InterventionCard key={intervention.id} intervention={intervention} />
                  ))}
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell>Démarche</TableCell>
                        <TableCell>Téléphone</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell align="center">Notes action</TableCell>
                        <TableCell align="center">Erreurs monitoring</TableCell>
                        <TableCell>Date limite</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {interventions.map((intervention: InterventionRequise) => (
                        <TableRow key={intervention.id}>
                          <TableCell>
                            {intervention.client_nom} {intervention.client_prenom}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{intervention.titre}</Typography>
                            {intervention.type_demarche_nom && (
                              <Typography variant="caption" color="textSecondary">
                                {intervention.type_demarche_nom}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>{intervention.telephone_principal || '-'}</TableCell>
                          <TableCell>
                            <Chip label={intervention.statut} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={intervention.nb_notes_action_recentes}
                              color={intervention.nb_notes_action_recentes > 0 ? 'warning' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={intervention.nb_erreurs_monitoring_recentes}
                              color={intervention.nb_erreurs_monitoring_recentes > 0 ? 'error' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {intervention.date_limite
                              ? new Date(intervention.date_limite).toLocaleDateString('fr-FR')
                              : '-'}
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="Voir la démarche">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => navigate(`/demarches/${intervention.id}`)}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Erreurs de monitoring */}
          {activeTab === 1 && (
            <>
              {monitoringErrors.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Aucune erreur de monitoring
                  </Typography>
                </Paper>
              ) : isMobile ? (
                <Box>
                  {monitoringErrors.map((errorItem: MonitoringError) => (
                    <MonitoringErrorCard key={errorItem.id} error={errorItem} />
                  ))}
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Démarche</TableCell>
                        <TableCell>Type vérification</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell>Message d'erreur</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {monitoringErrors.map((errorItem: MonitoringError) => (
                        <TableRow key={errorItem.id}>
                          <TableCell>
                            {new Date(errorItem.created_at).toLocaleString('fr-FR')}
                          </TableCell>
                          <TableCell>
                            {errorItem.client_nom} {errorItem.client_prenom}
                            <br />
                            <Typography variant="caption" color="textSecondary">
                              {errorItem.telephone_principal}
                            </Typography>
                          </TableCell>
                          <TableCell>{errorItem.demarche_titre}</TableCell>
                          <TableCell>
                            <Chip label={errorItem.type_verification} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            {errorItem.ancien_statut && errorItem.nouveau_statut ? (
                              <Box>
                                <Typography variant="caption" color="textSecondary">
                                  {errorItem.ancien_statut} → {errorItem.nouveau_statut}
                                </Typography>
                              </Box>
                            ) : (
                              <Chip label={errorItem.demarche_statut} size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="error">
                              {errorItem.error_message || '-'}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* AGDREF en attente */}
          {activeTab === 2 && (
            <>
              {agdrefPending.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Aucun compte en attente d'AGDREF
                  </Typography>
                </Paper>
              ) : isMobile ? (
                <Box>
                  {agdrefPending.map((client: AgdrefPending) => (
                    <AgdrefCard key={client.id} client={client} />
                  ))}
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Client</TableCell>
                        <TableCell>Téléphone</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Type compte</TableCell>
                        <TableCell>AGDREF</TableCell>
                        <TableCell>Démarche liée</TableCell>
                        <TableCell align="center">Alertes</TableCell>
                        <TableCell>Date création</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {agdrefPending.map((client: AgdrefPending) => (
                        <TableRow key={client.id}>
                          <TableCell>
                            {client.nom} {client.prenom}
                          </TableCell>
                          <TableCell>
                            {client.telephone_principal || '-'}
                            {client.est_numero_etranger && (
                              <Chip label="Étranger" size="small" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>{client.email || '-'}</TableCell>
                          <TableCell>
                            <Chip
                              label={client.numero_agdref ? 'Définitif' : 'Temporaire'}
                              color={client.numero_agdref ? 'success' : 'warning'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{client.numero_agdref || 'En attente'}</TableCell>
                          <TableCell>
                            {client.demarche_titre ? (
                              <Box>
                                <Typography variant="body2">{client.demarche_titre}</Typography>
                                <Chip label={client.demarche_statut} size="small" />
                              </Box>
                            ) : (
                              '-'
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={client.nb_alertes_agdref}
                              color={client.nb_alertes_agdref > 0 ? 'error' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {new Date(client.created_at).toLocaleDateString('fr-FR')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Activité récente */}
          {activeTab === 3 && (
            <>
              {recentActivity.length === 0 ? (
                <Paper sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="textSecondary">
                    Aucune activité récente
                  </Typography>
                </Paper>
              ) : isMobile ? (
                <Box>
                  {recentActivity.map((activity: RecentActivity) => (
                    <ActivityCard key={`${activity.type}-${activity.id}`} activity={activity} />
                  ))}
                </Box>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Démarche</TableCell>
                        <TableCell>Détails</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recentActivity.map((activity: RecentActivity) => (
                        <TableRow key={`${activity.type}-${activity.id}`}>
                          <TableCell>
                            {new Date(activity.created_at).toLocaleString('fr-FR')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={activity.type === 'note' ? 'Note' : 'Monitoring'}
                              color={activity.type === 'note' ? 'primary' : 'secondary'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            {activity.client_nom} {activity.client_prenom}
                          </TableCell>
                          <TableCell>{activity.demarche_titre}</TableCell>
                          <TableCell>
                            {activity.type === 'note' ? (
                              <Box>
                                <Chip
                                  label={getThemeLabel(activity.theme || '')}
                                  color={getThemeColor(activity.theme || '') as any}
                                  size="small"
                                  sx={{ mb: 0.5 }}
                                />
                                {activity.est_automatique && (
                                  <Chip label="Auto" size="small" sx={{ ml: 0.5, mb: 0.5 }} />
                                )}
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {activity.contenu}
                                </Typography>
                              </Box>
                            ) : (
                              <Box>
                                <Chip
                                  label={activity.type_verification}
                                  size="small"
                                  variant="outlined"
                                />
                                {activity.ancien_statut && activity.nouveau_statut && (
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    {activity.ancien_statut} → {activity.nouveau_statut}
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default CyberDashboard;
